import { Component, Prop, Event, EventEmitter, Method, State, Element, Host, h } from '@stencil/core';
import has from 'lodash-es/has';
import { Color, Size } from '../../interface';
import { createColorClasses } from '../../utils/theme';
import Swiper from 'swiper';
import { Pagination, Manipulation } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
import { DataIn } from './rtsp-h264-mjpeg-interface';
import { createNotify } from '../internal/notify/notify-controller';
import { createVideoModal } from '../internal/video-modal/video-modal-controller';
import { Camera, DEFAULT_UPLINK } from './camera';

@Component({
  tag: 'lar-rtsp-h264-mjpeg',
  styleUrl: 'rtsp-h264-mjpeg.scss',
  shadow: true
})
export class LarRTSPH264MJPEG {
  @Element() el!: HTMLLarRtspH264MjpegElement;
  /////// LarvaNode base properties
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop() color?: Color;
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop() colorModal?: Color;
  /**
   * The color to use from your application's color palette for small icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Hide camera names top of streams
   */
  @Prop() hideCameraNames: boolean = false
  /**
   * Hide node titles
   */
  @Prop() hideTitles: boolean = false
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Use upstream proxy for cameras
   */
  @Prop() useUplinkProxy: boolean = true;
  /**
   * server for cameras
   */
  @Prop({ mutable: true }) server!: string;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end
  @State() loading: boolean = true;
  @State() showPager: boolean = false;

  private cameras: Camera[] = [];
  private swiper!: Swiper;
  private paginationEl?: HTMLElement;
  private loadingListenerElement?: HTMLElement;

   /**
    * Emitted before the modal has presented.
    */
  @Event({ eventName: 'larnodewillpresent' }) willPresent!: EventEmitter;

   /**
    * Emitted after the modal has presented.
    */
  @Event({ eventName: 'larnodedidpresent' }) didPresent!: EventEmitter;

  /**
   * Emitted before the modal has dismissed.
   */
  @Event({ eventName: 'larnodewilldismiss' }) willDismiss!: EventEmitter;

  /**
   * Emitted after the modal has dismissed.
   */
  @Event({ eventName: 'larnodediddismiss' }) didDismiss!: EventEmitter;

  private normalizeSwiperOptions(): SwiperOptions {
    const swiperOptions: SwiperOptions = {
      modules: [Pagination, Manipulation],
      effect: 'slide',
      direction: 'horizontal',
      initialSlide: 0,
      loop: false,
      parallax: false,
      slidesPerView: 1,
      spaceBetween: 5,
      speed: 300,
      touchEventsTarget: 'container',
      autoplay: false,
      pagination: {
        el: this.paginationEl!,
        type: 'bullets',
        bulletElement: 'span',
        dynamicBullets: false,
        clickable: true,
        hideOnClick: false,
      },
    };

    return swiperOptions;
  }

  componentWillLoad() {
    this.close(); // dynamic update
    this.videoElementLoading = this.videoElementLoading.bind(this);
    if (!this.server && !this.useUplinkProxy) { // we dont have to use uplink proxy and we dont have device url defined; generate from current host
      this.server = Camera.getLocalVideoServer();
    } else if (!this.server) { // we do have to use uplink, but no server defined, use default
      this.server = DEFAULT_UPLINK;
    }
  }

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    const swiperel = el.querySelector('.swiper') as HTMLElement;
    const swiperOptions = this.normalizeSwiperOptions();
    this.swiper = new Swiper(swiperel, swiperOptions);
    // start stream if slide shown
    this.swiper.on('slideChange', () => this.startActiveSlideStream());
    this.output.emit(); // read camera list
  }

  disconnectedCallback() {
    try {
      this.close();
    } catch (err) {
      console.warn(err);
    }
  }

  /**
   * Larva error input
   */
  @Method()
  async error(data: any, timeout: number = 5000) {
    let msg: string;
    if (typeof data === 'string') {
      msg = data;
    } else if (data && data.message && typeof data.message === 'string') {
      msg = data.message;
    } else {
      msg = 'error_messages.UNKNOWN_ERROR';
    }
    const translate = <lar-translate t={msg} />;
    const notify = await createNotify({ message: translate, color: 'danger', autoHide: timeout });
    await notify.present();
    this.loading = false;
  }
  /**
   * Input Larva event message (see above)
   */
  @Method()
  async input(data: DataIn) {
    const el = this.el.shadowRoot || this.el;
    if (has(data, 'cameras')) {
      this.closeCameras();
      const cameras = Array.isArray(data.cameras) ? data.cameras : [];
      this.cameras = cameras.map(camera => new Camera(camera, this.server));
      this.showPager = this.cameras.length > 1;
      this.updateSwiper();
    } else if (has(data, 'upstream')) {
      // asked upstream ready message
      // start camerastream via uplink proxy
      await this.cameras.find(cam => cam.wsurl === data.upstream)?.open().catch(err => this.error(err));
    }
  }

  private close() {
    this.closeCameras();
    this.swiper?.off('click');
    this.swiper?.off('slideChange');
    this.swiper?.removeAllSlides();
    this.swiper?.destroy();
  }

  private closeCameras() {
    this.cameras.forEach(camera => camera.close()); // close all cameras
    this.cameras = [];
  }

  private async openActiveSlideCameraModal() {
    try {
      if (!this.swiper?.activeIndex === undefined) {
        throw new Error('Swiper not found');
      }
      const slide = this.swiper?.slides[this.swiper?.activeIndex];
      const element = slide?.querySelector('canvas,video');
      const cameraId = element?.getAttribute(Camera.elementCameraIdAttribute);
      if (!cameraId) {
        throw new Error('Camera ID not found');
      }
      const camera = this.cameras.find(cam => cam.id === cameraId);
      if (camera && !camera.loading) {
          camera.volume();
          const modal = await createVideoModal({
            color: this.colorModal || this.color,
            mainTitle: camera.options.name,
            onLarmodalwillpresent: () => { this.willPresent.emit(); },
            onLarmodaldidpresent: () => { this.didPresent.emit(); },
            onLarmodalwilldismiss: () => {
              this.willDismiss.emit();
              camera.mute();
              slide.appendChild(camera.$el);
            },
            onLarmodaldiddismiss: () => { this.didDismiss.emit(); },
          });
          modal.present(camera.$el);
        } else {
          throw new Error('Camera not found');
        }
      } catch (err) {
        console.warn(err);
      }
    }

  private videoElementLoading(event: CustomEvent) {
    this.loading = event.detail;
    // tslint:disable-next-line: no-unbound-method
    this.loadingListenerElement?.removeEventListener('larvideoloading', this.videoElementLoading);
    this.loadingListenerElement = undefined;
  }

  private startActiveSlideStream() {
    try {
      // tslint:disable-next-line: no-unbound-method
      this.loadingListenerElement?.removeEventListener('larvideoloading', this.videoElementLoading);
      this.loadingListenerElement = undefined;
      if (!this.swiper?.activeIndex === undefined) {
        throw new Error('Swiper not found');
      }
      const slide = this.swiper?.slides[this.swiper?.activeIndex];
      const element = slide?.querySelector('canvas,video');
      const cameraId = element?.getAttribute(Camera.elementCameraIdAttribute);
      if (!cameraId) {
        throw new Error('Camera ID not found');
      }
      const camera = this.cameras.find(cam => cam.id === cameraId);
      this.loading = camera.loading;
      if (this.loading) {
        this.loadingListenerElement = camera.$el;
        // tslint:disable-next-line: no-unbound-method
        this.loadingListenerElement?.addEventListener('larvideoloading', this.videoElementLoading);
      }
      if (camera && !camera.opened) {
        if (this.useUplinkProxy === true) {
          // we need to use uplinx proxy, ask for upstream
          this.output.emit({ upstream: camera.wsurl });
        } else if (camera) {
          // start streamming right away
          camera.open().catch(err =>this.error(err));
        } else {
          throw new Error('Camera not found from list');
        }
      }
    } catch (err) {
      console.warn(err);
    }
  }

  private updateSwiper() {
    const slides = this.cameras.map(camera => {
      const container = document.createElement('div');
      const title = document.createElement('div');
      container.className = 'swiper-slide';
      title.className = 'swiper-slide-title';
      title.innerHTML = camera.options.name;
      container.appendChild(camera.$el);
      container.appendChild(title);
      return container;
    });
    this.swiper?.removeAllSlides();
    if (slides.length > 0) {
      this.swiper?.appendSlide(slides);
      this.startActiveSlideStream();
    }
    this.swiper?.update();
    this.swiper?.pagination?.update();
  }

  render() {
    const iconSmall: HTMLLarIconElement | undefined = this.loading ? <lar-icon color={this.colorIconSmall || ''} icon="loading" /> : undefined;
    return (
      <Host class={createColorClasses(this.color)}>
        {this.nodeSize === 'small' && <h3>Camera small node not available</h3>}
        {this.nodeSize !== 'small' && <div class="lar-node-box" onClick={() => this.openActiveSlideCameraModal()}>
          <div class="swiper">
            <div class="swiper-wrapper"></div>
            <div class={{
              'swiper-pagination': true,
              'swiper-pagination-hidden': !this.showPager
            }} ref={el => this.paginationEl = el}></div>
          </div>
          {/* important that updateable elements are here, so swipper wrapper wont be removed from dom after render, otherwse swiper just stops working */}
          {iconSmall && <div class="lar-icon-small">{iconSmall}</div>}
        </div>}
      </Host>
    );
  }
}
